import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import {
  ORDERS_DECOPROF_EXCLUDED_SUPPLIERS,
  mapSupplierToType,
  type OrderDecoprofType,
  type OrderDecoprofStatus,
} from "@/lib/ordersDecoprof";

export interface OrdersDecoprofRow {
  ordernumber: string;
  orderid: number | null;
  primarySupplier: string | null;
  suppliers: string[];
  type: OrderDecoprofType;
  status: OrderDecoprofStatus;
  /** R52: createdat behouden zodat realtime-INSERTs op de juiste plek komen */
  createdAt: string | null;
}

interface RawRow {
  orderid: number | null;
  ordernumber: string | null;
  suppliertitle: string | null;
  producttitle: string | null;
  orders_decoprof_status: string | null;
  createdat: string | null;
  status: string | null;
}

// R57 — FIX 2: Lightspeed-statussen die "afgerond" betekenen. Orders met
// deze status verdwijnen automatisch uit de Decoprof-lijst (Mark's wens 4-mei).
// R72: "Geannuleerd" expliciet bevestigd op Mark's verzoek (mail 6-mei).
// Bron-bewijs (DB-distinct, supplier ≠ NULL):
//   packed_completed (754), completed_picked_up (15), completed_shipped (9),
//   cancelled (560 hist.) → uitsluiten. processing_* blijft tonen.
const LIGHTSPEED_COMPLETED_STATUSES = [
  "packed_completed",
  "completed_picked_up",
  "completed_shipped",
  "cancelled",
  // R72: defensieve aliassen voor toekomstige Lightspeed-varianten
  "canceled",
  "Geannuleerd",
] as const;
const LIGHTSPEED_COMPLETED_SET: ReadonlySet<string> = new Set(LIGHTSPEED_COMPLETED_STATUSES);

const QUERY_KEY = ["orders-decoprof"] as const;
const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;
const MAX_VISIBLE_ORDERS = 2000;

const isPlaceholder = (row: RawRow) => row.producttitle === "No products";
const isWithinWindow = (createdat: string | null) => {
  if (!createdat) return true; // wees ruim — laat aggregator beslissen
  const t = Date.parse(createdat);
  if (Number.isNaN(t)) return true;
  return Date.now() - t <= SIXTY_DAYS_MS;
};

const createdAtMs = (value: string | null) => {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const compareOrdersByCreatedAtDesc = (a: OrdersDecoprofRow, b: OrdersDecoprofRow) =>
  createdAtMs(b.createdAt) - createdAtMs(a.createdAt);

const insertSortedOrder = (list: OrdersDecoprofRow[], row: OrdersDecoprofRow) => {
  let low = 0;
  let high = list.length;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (compareOrdersByCreatedAtDesc(row, list[mid]) < 0) low = mid + 1;
    else high = mid;
  }
  list.splice(low, 0, row);
  if (list.length > MAX_VISIBLE_ORDERS) list.length = MAX_VISIBLE_ORDERS;
  return list;
};

export const useOrdersDecoprof = () => {
  const { isAuthenticated, isSessionValid, isValidating } = useAuth();
  const qc = useQueryClient();
  const sinceIsoRef = useRef(new Date(Date.now() - SIXTY_DAYS_MS).toISOString());

  const query = useQuery({
    queryKey: QUERY_KEY,
    staleTime: 30_000,
    enabled: isAuthenticated && isSessionValid && !isValidating,
    queryFn: async (): Promise<OrdersDecoprofRow[]> => {
      const excludedList = Array.from(ORDERS_DECOPROF_EXCLUDED_SUPPLIERS);
      const excludedCsv = `(${excludedList.map((s) => `"${s}"`).join(",")})`;

      // R52: limit verlaagd van 10000 → 2000 (gecombineerd met virtualisatie)
      // R57 — FIX 2: filter Lightspeed-afgeronde orders weg (status NOT IN ...)
      const completedCsv = `(${LIGHTSPEED_COMPLETED_STATUSES.map((s) => `"${s}"`).join(",")})`;
      const { data, error } = await supabase
        .from("orders_live_ls")
        .select("orderid, ordernumber, suppliertitle, producttitle, orders_decoprof_status, createdat, status")
        .gte("createdat", sinceIsoRef.current)
        .not("suppliertitle", "is", null)
        .neq("suppliertitle", "")
        .neq("producttitle", "No products")
        .not("suppliertitle", "in", excludedCsv)
        .not("status", "in", completedCsv)
        .order("createdat", { ascending: false })
        .limit(MAX_VISIBLE_ORDERS);

      if (error) {
        console.error("[useOrdersDecoprof] Error:", error);
        throw error;
      }

      const rows = (data ?? []) as RawRow[];
      return aggregateRows(rows);
    },
  });

  // ---- R52: Realtime channel — INSERT/UPDATE/DELETE patches in cache --------
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  useEffect(() => {
    if (!isAuthenticated || !isSessionValid || isValidating) return;

    const channel = supabase
      .channel("orders-decoprof-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders_live_ls" },
        (payload) => {
          const eventType = payload.eventType;
          const newRow = payload.new as RawRow | null;
          const oldRow = payload.old as RawRow | null;

          if (eventType === "DELETE") {
            const ordernumber = oldRow?.ordernumber;
            if (!ordernumber) return;
            qc.setQueryData<OrdersDecoprofRow[]>(QUERY_KEY, (prev) =>
              (prev ?? []).filter((o) => o.ordernumber !== ordernumber),
            );
            return;
          }

          if (!newRow || !newRow.ordernumber) return;
          const supplier = newRow.suppliertitle?.trim() || null;

          // Filter: alleen relevante (niet placeholder, niet uitgesloten supplier, binnen window)
          if (isPlaceholder(newRow)) return;
          if (!supplier) return;
          if (ORDERS_DECOPROF_EXCLUDED_SUPPLIERS.has(supplier)) return;
          if (!isWithinWindow(newRow.createdat)) return;

          // R57 — FIX 2: als de Lightspeed-status nu "afgerond" is, verwijder
          // de order uit de cache (UPDATE-event waarbij order net afgerond
          // werd in Lightspeed). Pas dan ook geen INSERT toe.
          if (newRow.status && LIGHTSPEED_COMPLETED_SET.has(newRow.status)) {
            qc.setQueryData<OrdersDecoprofRow[]>(QUERY_KEY, (prev) =>
              (prev ?? []).filter((o) => o.ordernumber !== newRow.ordernumber),
            );
            return;
          }

          qc.setQueryData<OrdersDecoprofRow[]>(QUERY_KEY, (prev) => {
            const list = prev ? [...prev] : [];
            const idx = list.findIndex((o) => o.ordernumber === newRow.ordernumber);

            if (idx === -1) {
              // INSERT — nieuwe order op exacte createdAt-positie (DESC) i.p.v. blind prepend.
              const inserted: OrdersDecoprofRow = {
                ordernumber: newRow.ordernumber!,
                orderid: newRow.orderid ?? null,
                primarySupplier: supplier,
                suppliers: [supplier],
                type: mapSupplierToType(supplier),
                status: (newRow.orders_decoprof_status as OrderDecoprofStatus) || "Nieuw",
                createdAt: newRow.createdat ?? null,
              };
              // Flash-event voor UI
              if (typeof window !== "undefined") {
                window.dispatchEvent(
                  new CustomEvent("orders-decoprof:flash", {
                    detail: { ordernumber: newRow.ordernumber },
                  }),
                );
              }
              return insertSortedOrder(list, inserted);
            }

            // UPDATE / aanvullende INSERT-regel op bestaande order
            const existing = list[idx];
            const suppliers = existing.suppliers.includes(supplier)
              ? existing.suppliers
              : [...existing.suppliers, supplier];
            const status =
              (newRow.orders_decoprof_status as OrderDecoprofStatus) || existing.status;
            const updated: OrdersDecoprofRow = {
              ...existing,
              orderid: existing.orderid ?? newRow.orderid ?? null,
              suppliers,
              status,
              createdAt: existing.createdAt ?? newRow.createdat ?? null,
            };
            list.splice(idx, 1);
            return insertSortedOrder(list, updated);
          });
        },
      )
      .subscribe((status) => {
        console.log("[useOrdersDecoprof] realtime status:", status);
      });

    channelRef.current = channel;
    return () => {
      try { supabase.removeChannel(channel); } catch { /* noop */ }
      channelRef.current = null;
    };
  }, [isAuthenticated, isSessionValid, isValidating, qc]);

  return query;
};

// Hulp: aggregator gebruikt door initial fetch
function aggregateRows(rows: RawRow[]): OrdersDecoprofRow[] {
  const byOrder = new Map<
    string,
    {
      orderid: number | null;
      suppliers: Set<string>;
      firstNonExcludedSupplier: string | null;
      status: OrderDecoprofStatus;
      createdAt: string | null;
    }
  >();

  for (const row of rows) {
    if (!row.ordernumber) continue;
    const supplier = row.suppliertitle?.trim() || null;

    let entry = byOrder.get(row.ordernumber);
    if (!entry) {
      entry = {
        orderid: row.orderid ?? null,
        suppliers: new Set<string>(),
        firstNonExcludedSupplier: null,
        status: (row.orders_decoprof_status as OrderDecoprofStatus) || "Nieuw",
        createdAt: row.createdat ?? null,
      };
      byOrder.set(row.ordernumber, entry);
    } else if (entry.orderid == null && row.orderid != null) {
      entry.orderid = row.orderid;
    }

    if (supplier) {
      entry.suppliers.add(supplier);
      if (
        !entry.firstNonExcludedSupplier &&
        !ORDERS_DECOPROF_EXCLUDED_SUPPLIERS.has(supplier)
      ) {
        entry.firstNonExcludedSupplier = supplier;
      }
    }
  }

  const result: OrdersDecoprofRow[] = [];
  for (const [ordernumber, entry] of byOrder.entries()) {
    const hasIncludedSupplier = Array.from(entry.suppliers).some(
      (s) => !ORDERS_DECOPROF_EXCLUDED_SUPPLIERS.has(s),
    );
    if (!hasIncludedSupplier) continue;
    const primary = entry.firstNonExcludedSupplier;
    result.push({
      ordernumber,
      orderid: entry.orderid,
      primarySupplier: primary,
      suppliers: Array.from(entry.suppliers),
      type: mapSupplierToType(primary),
      status: entry.status,
      createdAt: entry.createdAt,
    });
  }
  return result.sort(compareOrdersByCreatedAtDesc);
}

/**
 * R52: kleine hook die luistert naar het flash-event en een set van te
 * highlighten ordernummers exporteert. Tegel-component gebruikt dit om de
 * fade-in animatie ~1.5s te tonen.
 */
export const useOrdersDecoprofFlash = (): Set<string> => {
  const [flashing, setFlashing] = useState<Set<string>>(new Set());
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail as { ordernumber?: string } | undefined;
      const ordernumber = detail?.ordernumber;
      if (!ordernumber) return;
      setFlashing((prev) => {
        const next = new Set(prev); next.add(ordernumber); return next;
      });
      setTimeout(() => {
        setFlashing((prev) => {
          if (!prev.has(ordernumber)) return prev;
          const next = new Set(prev); next.delete(ordernumber); return next;
        });
      }, 1500);
    };
    window.addEventListener("orders-decoprof:flash", handler);
    return () => window.removeEventListener("orders-decoprof:flash", handler);
  }, []);
  return flashing;
};

export const useUpdateOrdersDecoprofStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      ordernumber?: string;
      ordernumbers?: string[];
      status: OrderDecoprofStatus;
    }) => {
      const list = params.ordernumbers && params.ordernumbers.length > 0
        ? params.ordernumbers
        : params.ordernumber
          ? [params.ordernumber]
          : [];
      if (list.length === 0) return { ordernumbers: [] as string[], status: params.status };
      const { error } = await supabase
        .from("orders_live_ls")
        .update({ orders_decoprof_status: params.status })
        .in("ordernumber", list);
      if (error) throw error;
      return { ordernumbers: list, status: params.status };
    },
    onSuccess: (result) => {
      if (!result || result.ordernumbers.length === 0) return;
      qc.setQueryData<OrdersDecoprofRow[]>(QUERY_KEY, (prev) =>
        (prev ?? []).map((row) =>
          result.ordernumbers.includes(row.ordernumber)
            ? { ...row, status: result.status }
            : row,
        ),
      );
    },
  });
};
